import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './ProductSearch.css';

interface ProductSearchProps {
  placeholder?: string;
}

const ProductSearch: React.FC<ProductSearchProps> = ({
  placeholder = 'Search products...',
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchQuery, setSearchQuery] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  });

  /*
   * Keep the search box synchronized with the URL.
   *
   * Example:
   * /products?search=shoe
   *
   * When the URL changes, the input changes too.
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchFromUrl = params.get('search') || '';

    setSearchQuery(searchFromUrl);
  }, [location.search]);

  /*
   * Submit search.
   *
   * Pressing Enter / Go on the mobile keyboard
   * submits this form.
   */
  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const query = searchQuery.trim();

    if (!query) {
      navigate('/products');
      return;
    }

    navigate(`/products?search=${encodeURIComponent(query)}`);
  };

  /*
   * Clear search.
   */
  const handleClear = () => {
    setSearchQuery('');

    if (location.pathname === '/products') {
      navigate('/products');
    }
  };

  return (
    <div className="product-search-container">
      <form
        className="product-search-form"
        onSubmit={handleSearch}
      >
        <div className="product-search-box">
          <span
            className="product-search-icon"
            aria-hidden="true"
          >
            🔍
          </span>

          <input
            type="search"
            className="product-search-input"
            placeholder={placeholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoComplete="off"
            enterKeyHint="search"
            aria-label="Search products"
          />

          {searchQuery && (
            <button
              type="button"
              className="product-search-clear"
              onClick={handleClear}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ProductSearch;